/**
 * Random events scheduler — see `docs/16_random_sensory_effects.md`.
 *
 * Pure functions only — the React side (useRandomEvent) holds the state.
 * Keeping the math pure lets us unit-test rates without rendering.
 *
 * Contract:
 *   - `maybeFireOnTap` is called from the game store after every tap.
 *   - It returns either a newly-emitted Effect, or null if no effect fires.
 *   - The caller is responsible for tracking `activeEffects` and cooldown
 *     timestamps (`lastFiredById`, `lastFiredByClass`).
 *
 * Anti-fatigue rules enforced:
 *   1. Per-effect cooldown (`cooldownSameMs`).
 *   2. Per-class cooldown (`cooldownClassMs`).
 *   3. Never two effects active at the same time (caller checks
 *      `activeEffects.length` before calling us).
 *   4. Random ramp-up: first hour of play has effects fire at ×0.5 rate
 *      so newcomers can settle into the basic feel first.
 */
import { ALPHA_EFFECTS, type EffectDefinition } from './catalog.js';

export interface ActiveEffect {
  readonly id: string;
  readonly class: EffectDefinition['class'];
  readonly emittedAt: number; // ms epoch
  readonly endsAt: number; // ms epoch (emittedAt + durationMs)
}

export interface SchedulerContext {
  /** Player's current epoch (1-indexed). */
  readonly epoch: number;
  /** Total taps over the lifetime (for ramp-up calc). */
  readonly totalTaps: number;
  /** Last-fired timestamp per effect id (ms epoch). */
  readonly lastFiredById: Readonly<Record<string, number>>;
  /** Last-fired timestamp per class (ms epoch). */
  readonly lastFiredByClass: Readonly<Record<string, number>>;
  /** Effects currently still active (their end-time > now). */
  readonly activeEffects: readonly ActiveEffect[];
  /** Current time in ms epoch. */
  readonly now: number;
  /** PRNG — default Math.random; tests inject a deterministic one. */
  readonly random?: () => number;
}

/** First 1000 taps get a 0.5x rate so onboarding stays calm. */
const RAMP_UP_TAPS = 1000;
const RAMP_UP_RATE = 0.5;

/**
 * Decide whether any random effect should fire on this tap.
 * Returns the effect to fire, or null if none.
 */
export function maybeFireOnTap(ctx: SchedulerContext): ActiveEffect | null {
  // Never fire if another effect is still active.
  if (ctx.activeEffects.length > 0) return null;

  const random = ctx.random ?? Math.random;
  const rampMultiplier = ctx.totalTaps < RAMP_UP_TAPS ? RAMP_UP_RATE : 1;

  // Eligibility: epoch threshold + same-effect cooldown + class cooldown.
  const eligible = ALPHA_EFFECTS.filter((e) => {
    if (e.epochMin !== undefined && ctx.epoch < e.epochMin) return false;
    const lastSame = ctx.lastFiredById[e.id] ?? 0;
    if (ctx.now - lastSame < e.cooldownSameMs) return false;
    const lastClass = ctx.lastFiredByClass[e.class] ?? 0;
    if (ctx.now - lastClass < e.cooldownClassMs) return false;
    return true;
  });

  if (eligible.length === 0) return null;

  // Each eligible effect rolls independently. To avoid bias by list order,
  // we iterate in a random permutation and stop at the first hit.
  const shuffled = [...eligible].sort(() => random() - 0.5);
  for (const e of shuffled) {
    if (random() < e.baseProb * rampMultiplier) {
      return {
        id: e.id,
        class: e.class,
        emittedAt: ctx.now,
        endsAt: ctx.now + e.durationMs,
      };
    }
  }
  return null;
}

/** Remove effects that have ended. */
export function pruneExpired(
  active: readonly ActiveEffect[],
  now: number,
): readonly ActiveEffect[] {
  return active.filter((e) => e.endsAt > now);
}
